import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
  useTheme
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useAgent } from '../contexts/AgentContext';
import { agentApiClient } from '../utils/agentApiClient';

const AgentChat: React.FC = () => {
  const theme = useTheme();
  const { state, dispatch } = useAgent();
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [state.messages]);

  const handleSend = async () => {
    if (!input.trim()) return;

    try {
      setIsLoading(true);
      dispatch({
        type: 'ADD_MESSAGE',
        payload: { type: 'user', content: input }
      });

      const response = await agentApiClient.sendMessage(input);

      if (response.success) {
        dispatch({
          type: 'ADD_MESSAGE',
          payload: { 
            type: 'agent', 
            content: response.message 
          }
        });

        if (response.data) {
          dispatch({
            type: 'SET_TASK',
            payload: response.data
          });
        }
      } else {
        dispatch({
          type: 'ADD_MESSAGE',
          payload: { 
            type: 'error', 
            content: response.error || 'Failed to get response from agent' 
          }
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      dispatch({
        type: 'ADD_MESSAGE',
        payload: { 
          type: 'error', 
          content: 'Failed to send message to agent' 
        }
      });
    } finally {
      setIsLoading(false);
      setInput('');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        gap: 2
      }}
    >
      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          padding: 2
        }}
      >
        {state.messages.map((msg, index) => (
          <Box
            key={index}
            sx={{
              alignSelf: msg.type === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '80%'
            }}
          >
            <Paper
              elevation={1}
              sx={{
                padding: 2,
                backgroundColor: 
                  msg.type === 'user' 
                    ? theme.palette.primary.main
                    : msg.type === 'error'
                    ? theme.palette.error.light
                    : theme.palette.background.paper,
                color: 
                  msg.type === 'user' 
                    ? theme.palette.primary.contrastText
                    : msg.type === 'error'
                    ? theme.palette.error.contrastText
                    : theme.palette.text.primary
              }}
            >
              <Typography>{msg.content}</Typography>
            </Paper>
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box sx={{ display: 'flex', gap: 1, padding: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type your message..."
          value={input}
          onChange={(e) => {
            const value = e.target.value;
            // Ensure we're getting the full value including the first character
            setInput(value);
          }}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
          autoComplete="off"
          sx={{
            backgroundColor: theme.palette.background.paper,
            '& .MuiInputBase-input': {
              padding: '12px 14px', // Ensure consistent padding
            }
          }}
        />
        <IconButton 
          color="primary" 
          onClick={handleSend}
          disabled={isLoading || !input.trim()}
        >
          {isLoading ? (
            <CircularProgress size={24} />
          ) : (
            <SendIcon />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

export default AgentChat;
